/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, Routes } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/sui/SuiBox";
import SuiTypography from "components/sui/SuiTypography";

import { useTranslation } from "react-i18next";

import { BASE_PATH } from "settings";
import { useEffect } from "react";

function Breadcrumbs({ icon, title, route, light }) {
  const routes = route.slice(0, -1).map((e) => {
    return decodeURI(e);
  });
  title = decodeURI(title);
  const { t } = useTranslation("route");

  // Add to create correct path
  const toPath = (idx) => {
    return `${BASE_PATH}/${routes.slice(0, idx + 1).join("/")}`;
  };

  return (
    <SuiBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) =>
              light ? white.main : grey[600],
          },
        }}
      >
        <Link to={`${BASE_PATH}/home`}>
          <SuiTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </SuiTypography>
        </Link>
        {routes.map((el, idx) => (
          <Link to={toPath(idx)} key={`${el}#${idx}`}>
            <SuiTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform={
                routes.at(0) === "my-folder" && (el || "") !== "my-folder"
                  ? "none"
                  : "capitalize"
              }
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {routes.at(0) === "my-folder" && (el || "") !== "my-folder"
                ? el
                : t(el.replace(/-/g, " ").replace(/_/g, " "))}
            </SuiTypography>
          </Link>
        ))}
        <SuiTypography
          variant="button"
          fontWeight="regular"
          textTransform={
            routes.at(0) === "my-folder" && (title || "") !== "my-folder"
              ? "none"
              : "capitalize"
          }
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {routes.at(0) === "my-folder" && (title || "") !== "my-folder"
            ? title
            : t((title || "").replace(/-/g, " ").replace(/_/g, " "))}
        </SuiTypography>
      </MuiBreadcrumbs>
      <SuiTypography
        fontWeight="bold"
        textTransform={
          routes.at(0) === "my-folder" && (title || "") !== "my-folder"
            ? "none"
            : "capitalize"
        }
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {routes.at(0) === "my-folder" && (title || "") !== "my-folder"
          ? title
          : t((title || "").replace(/-/g, " ").replace(/_/g, " "))}
      </SuiTypography>
    </SuiBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
